import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, Plus, Pencil, Trash2, X, Save, Upload, Link as LinkIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '', icon: '', description: '', deep_link_url: '', active: true,
  input_type: 'username', allowed_prefixes: [], allowed_chars: 'any',
  min_length: '', max_length: '', regex_pattern: '', error_message: '',
};

/**
 * Renders the icon field verbatim: as an <img> when it's a URL / storage path,
 * otherwise as inline text (emoji or short label).
 */
const IconPreview = ({ icon, className = 'w-6 h-6' }) => {
  if (!icon) return <MessageCircle className={`${className} text-slate-300`} />;
  const isImage = icon.startsWith('http') || icon.startsWith('/api/media/') || icon.startsWith('/');
  if (isImage) {
    return <img src={icon} alt="" className={`${className} object-contain rounded`} />;
  }
  return <span className="text-2xl leading-none select-none">{icon}</span>;
};

const MessagingAppsManagement = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null | 'new' | app.id
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchApps = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/messaging-apps`, { withCredentials: true });
      setApps(res.data.apps || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load messaging apps');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const startCreate = () => {
    setEditingId('new');
    setForm(emptyForm);
  };
  const startEdit = (app) => {
    setEditingId(app.id);
    setForm({
      name: app.name || '',
      icon: app.icon || '',
      description: app.description || '',
      deep_link_url: app.deep_link_url || '',
      active: !!app.active,
      input_type: app.input_type || 'username',
      allowed_prefixes: app.allowed_prefixes || [],
      allowed_chars: app.allowed_chars || 'any',
      min_length: app.min_length ?? '',
      max_length: app.max_length ?? '',
      regex_pattern: app.regex_pattern || '',
      error_message: app.error_message || '',
    });
  };
  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    const payload = {
      ...form,
      // Numeric fields come from <input type=number>; coerce empty string → null
      min_length: form.min_length === '' ? null : Number(form.min_length),
      max_length: form.max_length === '' ? null : Number(form.max_length),
    };
    setSaving(true);
    try {
      if (editingId === 'new') {
        await axios.post(`${BACKEND_URL}/api/admin/messaging-apps`, payload, { withCredentials: true });
        toast.success(`${form.name} added`);
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/messaging-apps/${editingId}`, payload, { withCredentials: true });
        toast.success(`${form.name} saved`);
      }
      cancelEdit();
      fetchApps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save messaging app');
    } finally {
      setSaving(false);
    }
  };

  const removeApp = async (app) => {
    if (!window.confirm(`Delete "${app.name}"? It will also be removed from every language that had it enabled.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/messaging-apps/${app.id}`, { withCredentials: true });
      toast.success(`${app.name} deleted`);
      fetchApps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-100 overflow-hidden" data-testid="messaging-apps-management">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <MessageCircle className="w-5 h-5 text-indigo-500" />
            Messaging Apps
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Configure the messaging apps that can appear on the contact form. Map them to specific languages under Language Management.
          </p>
        </div>
        <button
          onClick={startCreate}
          disabled={editingId === 'new'}
          data-testid="messaging-apps-add"
          className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> Add App
        </button>
      </div>

      <div className="divide-y divide-slate-100">
        {editingId === 'new' && (
          <AppForm form={form} setForm={setForm} onSave={save} onCancel={cancelEdit} saving={saving} label="New App" />
        )}
        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-slate-400">Loading…</div>
        ) : apps.length === 0 && editingId !== 'new' ? (
          <div className="px-5 py-10 text-center text-sm text-slate-400">
            No messaging apps yet. Add KakaoTalk, LINE, WhatsApp, Telegram, Zalo, etc.
          </div>
        ) : (
          apps.map(app => (
            editingId === app.id ? (
              <AppForm key={app.id} form={form} setForm={setForm} onSave={save} onCancel={cancelEdit} saving={saving} label={`Edit — ${app.name}`} />
            ) : (
              <div
                key={app.id}
                className="flex items-center gap-4 px-5 py-3"
                data-testid={`messaging-app-row-${app.id}`}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-slate-50 rounded-lg flex-shrink-0">
                  <IconPreview icon={app.icon} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 truncate">{app.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold ${
                      app.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {app.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {app.description && <p className="text-xs text-slate-500 truncate">{app.description}</p>}
                </div>
                <button
                  onClick={() => startEdit(app)}
                  data-testid={`messaging-app-edit-${app.id}`}
                  className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeApp(app)}
                  data-testid={`messaging-app-delete-${app.id}`}
                  className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )
          ))
        )}
      </div>
    </div>
  );
};

const AppForm = ({ form, setForm, onSave, onCancel, saving, label }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      if (form.icon && form.icon.startsWith('/api/media/images/')) {
        body.append('old_url', form.icon);
      }
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload-image`, body, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm({ ...form, icon: res.data.url });
      toast.success('Icon uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload icon');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="bg-indigo-50/40 px-5 py-4 space-y-3" data-testid="messaging-app-form">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <button onClick={onCancel} data-testid="messaging-app-form-close" className="p-1 text-slate-400 hover:text-slate-700 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            data-testid="messaging-app-form-name"
            placeholder="e.g. KakaoTalk"
            className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Icon</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={form.icon}
              onChange={e => setForm({ ...form, icon: e.target.value })}
              data-testid="messaging-app-form-icon"
              placeholder="Upload an image or paste emoji/URL"
              className="flex-1 text-sm px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="messaging-app-form-upload"
              title="Upload an icon image"
              className="inline-flex items-center gap-1 px-2 py-2 text-xs bg-white border border-slate-200 rounded-lg hover:border-indigo-400 hover:text-indigo-700 disabled:opacity-50"
            >
              <Upload className="w-4 h-4" /> {uploading ? '…' : 'Upload'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
              data-testid="messaging-app-form-file"
            />
            <div className="w-10 h-10 flex items-center justify-center bg-white border border-slate-200 rounded-lg">
              <IconPreview icon={form.icon} />
            </div>
          </div>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Description (optional)</label>
        <input
          type="text"
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          data-testid="messaging-app-form-description"
          placeholder="Short hint shown under the field on the contact form"
          className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">
          <span className="inline-flex items-center gap-1">
            <LinkIcon className="w-3.5 h-3.5" /> Deep-Link URL Template (optional)
          </span>
        </label>
        <input
          type="text"
          value={form.deep_link_url}
          onChange={e => setForm({ ...form, deep_link_url: e.target.value })}
          data-testid="messaging-app-form-deep-link"
          placeholder="https://t.me/{id}  ·  https://wa.me/{id}  ·  viber://chat?number={id}"
          className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 font-mono"
        />
        <p className="text-xs text-slate-500 mt-1">
          Use <code className="font-mono bg-slate-100 px-1 rounded">{'{id}'}</code> where the user's ID/number goes. Staff can click received IDs to open the app.
        </p>
      </div>
      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={form.active}
          onChange={e => setForm({ ...form, active: e.target.checked })}
          data-testid="messaging-app-form-active"
          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
        Active (visible on the contact form for mapped languages)
      </label>

      <details className="border border-slate-200 rounded-lg bg-white" data-testid="messaging-app-form-validation">
        <summary className="px-3 py-2 cursor-pointer text-sm font-medium text-slate-700 select-none">
          Validation rules <span className="text-xs text-slate-400 ml-1">(applied on the contact form)</span>
        </summary>
        <div className="p-3 space-y-3 border-t border-slate-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Input type</label>
              <select
                value={form.input_type}
                onChange={e => setForm({ ...form, input_type: e.target.value })}
                data-testid="messaging-app-form-input-type"
                className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-white"
              >
                <option value="username">Username</option>
                <option value="phone">Phone number (country code required)</option>
                <option value="phone_or_username">Phone number or username</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Allowed characters</label>
              <select
                value={form.allowed_chars}
                onChange={e => setForm({ ...form, allowed_chars: e.target.value })}
                data-testid="messaging-app-form-allowed-chars"
                className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-white"
              >
                <option value="any">Any</option>
                <option value="digits">Digits only</option>
                <option value="alphanumeric">Letters, digits, dots, underscores</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Allowed prefixes</label>
            <div className="flex gap-3">
              {['+', '00'].map(p => (
                <label key={p} className="inline-flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    checked={(form.allowed_prefixes || []).includes(p)}
                    onChange={e => {
                      const next = new Set(form.allowed_prefixes || []);
                      if (e.target.checked) next.add(p); else next.delete(p);
                      setForm({ ...form, allowed_prefixes: Array.from(next) });
                    }}
                    data-testid={`messaging-app-form-prefix-${p === '+' ? 'plus' : '00'}`}
                    className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="font-mono">{p}</span>
                </label>
              ))}
              <p className="text-xs text-slate-400 ml-2 self-center">(none checked ⇒ no prefix allowed)</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Min length</label>
              <input
                type="number"
                min="0"
                value={form.min_length}
                onChange={e => setForm({ ...form, min_length: e.target.value })}
                data-testid="messaging-app-form-min-length"
                className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Max length</label>
              <input
                type="number"
                min="0"
                value={form.max_length}
                onChange={e => setForm({ ...form, max_length: e.target.value })}
                data-testid="messaging-app-form-max-length"
                className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Regex pattern (optional)</label>
            <input
              type="text"
              value={form.regex_pattern}
              onChange={e => setForm({ ...form, regex_pattern: e.target.value })}
              data-testid="messaging-app-form-regex"
              placeholder="^[A-Za-z][A-Za-z0-9_]{4,31}$"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Custom error message (optional)</label>
            <input
              type="text"
              value={form.error_message}
              onChange={e => setForm({ ...form, error_message: e.target.value })}
              data-testid="messaging-app-form-error-message"
              placeholder="Shown to the user when their input fails the rules above."
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg"
            />
          </div>
        </div>
      </details>

      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          data-testid="messaging-app-form-cancel"
          className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="messaging-app-form-save"
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default MessagingAppsManagement;
